import React, { useEffect, useState, useRef } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { auth } from '../../../../firebase/config';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiCheck, FiX, FiMail, FiDownload, FiFileText, FiChevronDown, FiTrash2 } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import AddChangeOfVendorModal from '../components/AddChangeOfVendorModal/index';
import Modal from '../../../../components/Modal';
import { Markdown } from '../../../../components/Markdown';
import { fetchChangeOfVendorsData, updateChangeOfVendorStatus, updateDeploymentDate, fetchChangeOfVendorBankId, deleteChangeOfVendor, fetchAllDeployments, type ChangeOfVendor } from '../service/changeOfVendorService';
import {
    designationIsIncharge,
    designationIsMisIncharge,
    normalizeChangeOfVendorStatus,
} from '../../../../utils/designationRole';
import { useAppSelector } from '../../../../store';
import { getDocs, collection, doc, getDoc, query, where } from "firebase/firestore";
import { db } from '../../../../firebase/config';
import { fetchCompaniesData } from '../../../companies/service/companiesService';
import type { Company } from '../../../companies/service/companiesService';
import { sendEmailsWithErrorHandling, validateEmails } from '../../../../api/emailService';
import TextField from '../../../../components/TextField';
import Table, { Column } from '../../../../components/Table';
import ChipInput from '../../../../components/ChipInput';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';


type CovStatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'forwarded_to_head';

function startOfLocalDayYmd(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function endOfLocalDayYmd(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function getCovRequestDate(cov: ChangeOfVendor): Date | null {
    const rd = cov.requestDate as any;
    if (!rd) return null;
    if (typeof rd.toDate === 'function') return rd.toDate() as Date;
    try {
        return new Date(rd);
    } catch {
        return null;
    }
}

function matchesCovStatusFilter(filter: CovStatusFilter, raw: string | undefined): boolean {
    if (filter === 'all') return true;
    const n = normalizeChangeOfVendorStatus(raw);
    return n === filter;
}

interface ChangeOfVendorsProps {
    summaryMode?: boolean;
}

export default function ChangeOfVendors({ summaryMode = false }: ChangeOfVendorsProps) {
    const [changeOfVendors, setChangeOfVendors] = useState<ChangeOfVendor[]>([]);
    const [filtered, setFiltered] = useState<ChangeOfVendor[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedChangeOfVendor, setSelectedChangeOfVendor] = useState<ChangeOfVendor | null>(null);
    const [currentDeployedCompany, setCurrentDeployedCompany] = useState<Company | null>(null);
    const [loadingCurrentCompany, setLoadingCurrentCompany] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [resubmitRequest, setResubmitRequest] = useState<ChangeOfVendor | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingRequest, setRejectingRequest] = useState<ChangeOfVendor | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectError, setRejectError] = useState('');
    const [isRegionalRejection, setIsRegionalRejection] = useState(false);

    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingDeploymentDate, setEditingDeploymentDate] = useState<string | null>(null);
    const [editingDeploymentDateValue, setEditingDeploymentDateValue] = useState<string>('');
    const [updatingDeploymentDate, setUpdatingDeploymentDate] = useState<string | null>(null);
    const [companyTypes, setCompanyTypes] = useState<string[]>([]);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [requestDateFrom, setRequestDateFrom] = useState<string | null>(null);
    const [requestDateTo, setRequestDateTo] = useState<string | null>(null);
    const [draftDateFilter, setDraftDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [draftRequestDateFrom, setDraftRequestDateFrom] = useState<string | null>(null);
    const [draftRequestDateTo, setDraftRequestDateTo] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<CovStatusFilter>(() => {
        if (typeof sessionStorage === 'undefined') return 'all';
        const designation = sessionStorage.getItem('psoDesignation')?.trim();
        if (designation === 'Head') return 'forwarded_to_head';
        if (designation && /regional/i.test(designation)) return 'pending';
        return 'all';
    });
    const [draftStatusFilter, setDraftStatusFilter] = useState<CovStatusFilter>(() => {
        if (typeof sessionStorage === 'undefined') return 'all';
        const designation = sessionStorage.getItem('psoDesignation')?.trim();
        if (designation === 'Head') return 'forwarded_to_head';
        if (designation && /regional/i.test(designation)) return 'pending';
        return 'all';
    });
    const [companyTypeFilter, setCompanyTypeFilter] = useState<string>('all');
    const [draftCompanyTypeFilter, setDraftCompanyTypeFilter] = useState<string>('all');
    const [hasInitializedFilter, setHasInitializedFilter] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);

    // Email State
    const [activeEmailTab, setActiveEmailTab] = useState<'removal' | 'installation'>('removal');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);

    // Removal Email State
    const [removalRecipients, setRemovalRecipients] = useState<string[]>([]);
    const [removalSubject, setRemovalSubject] = useState('');
    const [removalBody, setRemovalBody] = useState('');
    const [removalBodyText, setRemovalBodyText] = useState('');

    // Installation Email State
    const [installationRecipients, setInstallationRecipients] = useState<string[]>([]);
    const [installationSubject, setInstallationSubject] = useState('');
    const [installationBody, setInstallationBody] = useState('');
    const [installationBodyText, setInstallationBodyText] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((s) => s.user.pso);
    const isRegionalHead = !!pso && /regional/i.test(pso.designation || '');
    /** Prefer Redux; fallback helps if designation is briefly missing before rehydrate. */
    const psoDesignation =
        (pso?.designation as string | undefined)?.trim() ||
        (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('psoDesignation')?.trim() || undefined : undefined);
    const isHead = psoDesignation === 'Head';
    const isIncharge = designationIsIncharge(psoDesignation);
    const canCreateRequest = !isIncharge && !isRegionalHead && !isHead; // Only PSO and Bank users can create requests
    const isPSO = !isBankUser && !isIncharge && !isRegionalHead && !isHead; // Regular PSO user

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        if (typeof sessionStorage === 'undefined') return;
        if (pso?.designation?.trim()) sessionStorage.setItem('psoDesignation', pso.designation.trim());
    }, [pso?.designation]);

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
                console.log('Loaded change of vendor data:', changeOfVendorsData);
                setChangeOfVendors(changeOfVendorsData);

                // Extract unique company types
                const typesSet = new Set<string>();
                changeOfVendorsData.forEach(changeOfVendor => {
                    if (changeOfVendor.company?.companyType) {
                        typesSet.add(changeOfVendor.company.companyType);
                    }
                });
                setCompanyTypes(Array.from(typesSet));
            } catch (err: any) {
                console.error('Error loading change of vendor data:', err);
                showToast('Failed to load change of vendor data', 'error');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser, isRegionalHead, psoDesignation]);

    useEffect(() => {
        if (hasInitializedFilter) return;
        if (uid) {
            if (userRole === 'pso' && pso === undefined) {
                return; // Wait for Redux to rehydrate PSO details
            }
            const defaultFilter = isRegionalHead ? 'pending' : 'all';
            setStatusFilter(defaultFilter);
            setDraftStatusFilter(defaultFilter);
            setHasInitializedFilter(true);
        }
    }, [uid, isRegionalHead, hasInitializedFilter, userRole, pso]);

    const hasActiveCovFilters =
        dateFilter !== 'all' ||
        !!(requestDateFrom && requestDateTo) ||
        statusFilter !== 'all' ||
        companyTypeFilter !== 'all';

    const applySearch = () => {
        setAppliedSearchTerm(searchInput.trim());
        setCurrentPage(1);
    };

    const openFilterModal = () => {
        setDraftDateFilter(dateFilter);
        setDraftRequestDateFrom(requestDateFrom);
        setDraftRequestDateTo(requestDateTo);
        setDraftStatusFilter(statusFilter);
        setDraftCompanyTypeFilter(companyTypeFilter);
        setShowFilterModal(true);
    };

    const applyFilters = () => {
        const useRange = !!(draftRequestDateFrom && draftRequestDateTo);
        if (useRange) {
            setDateFilter('all');
            setRequestDateFrom(draftRequestDateFrom);
            setRequestDateTo(draftRequestDateTo);
        } else {
            setRequestDateFrom(null);
            setRequestDateTo(null);
            setDateFilter(draftDateFilter);
        }
        setStatusFilter(draftStatusFilter);
        setCompanyTypeFilter(draftCompanyTypeFilter);
        setCurrentPage(1);
        setShowFilterModal(false);
    };

    const clearFilters = () => {
        setDateFilter('all');
        setRequestDateFrom(null);
        setRequestDateTo(null);
        const defaultFilter = isRegionalHead ? 'pending' : 'all';
        setStatusFilter(defaultFilter);
        setCompanyTypeFilter('all');
        setDraftDateFilter('all');
        setDraftRequestDateFrom(null);
        setDraftRequestDateTo(null);
        setDraftStatusFilter(defaultFilter);
        setDraftCompanyTypeFilter('all');
        setCurrentPage(1);
    };

    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [exportMenuOpen]);

    useEffect(() => {
        setCurrentPage(1);
    }, [appliedSearchTerm, dateFilter, requestDateFrom, requestDateTo, statusFilter, companyTypeFilter]);

    useEffect(() => {
        let list = [...changeOfVendors];

        if (companyTypeFilter !== 'all') {
            list = list.filter((c) => c.company?.companyType === companyTypeFilter);
        }

        if (summaryMode) {
            if (isHead) {
                list = list.filter((c) => normalizeChangeOfVendorStatus(c.status) === 'forwarded_to_head');
            } else if (isRegionalHead) {
                list = list.filter((c) => normalizeChangeOfVendorStatus(c.status) === 'pending');
            }
        } else {
            if (statusFilter !== 'all') {
                list = list.filter((c) => matchesCovStatusFilter(statusFilter, c.status));
            }
        }

        if (requestDateFrom && requestDateTo) {
            const fromMs = startOfLocalDayYmd(requestDateFrom).getTime();
            const toMs = endOfLocalDayYmd(requestDateTo).getTime();
            list = list.filter((c) => {
                const d = getCovRequestDate(c);
                if (!d) return false;
                const t = d.getTime();
                return t >= fromMs && t <= toMs;
            });
        } else if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            list = list.filter((c) => {
                const requestDate = getCovRequestDate(c);
                if (!requestDate) return false;
                const requestDateOnly = new Date(
                    requestDate.getFullYear(),
                    requestDate.getMonth(),
                    requestDate.getDate()
                );
                switch (dateFilter) {
                    case 'daily':
                        return requestDateOnly.getTime() === today.getTime();
                    case 'weekly': {
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return requestDateOnly >= weekAgo && requestDateOnly <= today;
                    }
                    case 'monthly': {
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return requestDateOnly >= monthAgo && requestDateOnly <= today;
                    }
                    default:
                        return true;
                }
            });
        }

        if (appliedSearchTerm) {
            const term = appliedSearchTerm.toLowerCase();
            list = list.filter((changeOfVendor) => {
                const requestId = (changeOfVendor.requestId || '').toString().toLowerCase();
                const branchCode = (changeOfVendor.branch?.branchCode || '').toString().toLowerCase();
                const branchAddress = (changeOfVendor.branch?.address || '').toString().toLowerCase();
                const companyName = (changeOfVendor.company?.companyName || '').toString().toLowerCase();
                const companyType = (changeOfVendor.company?.companyType || '').toString().toLowerCase();
                const companyId = (changeOfVendor.company?.companyId || '').toString().toLowerCase();
                const statusStr = (changeOfVendor.status || '').toString().toLowerCase();
                return (
                    requestId.includes(term) ||
                    branchCode.includes(term) ||
                    branchAddress.includes(term) ||
                    companyName.includes(term) ||
                    companyType.includes(term) ||
                    companyId.includes(term) ||
                    statusStr.includes(term)
                );
            });
        }

        setFiltered(list);
    }, [
        appliedSearchTerm,
        changeOfVendors,
        companyTypeFilter,
        dateFilter,
        requestDateFrom,
        requestDateTo,
        statusFilter,
        summaryMode,
        isHead,
        isRegionalHead,
    ]);

    const fetchCurrentDeployedCompany = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.branchId || !changeOfVendor.company) return;

        setLoadingCurrentCompany(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                setCurrentDeployedCompany(null);
                return;
            }

            // Get the company type from the new company
            const companyType = changeOfVendor.company.companyType;
            if (!companyType) {
                setCurrentDeployedCompany(null);
                return;
            }

            // Optimized: Fetch only deployments for this branch
            const deploymentsQuery = query(
                collection(db, "banks", bankId, "companyDeployment"),
                where("branchId", "==", changeOfVendor.branchId)
            );
            const deploymentsSnap = await getDocs(deploymentsQuery);
            const deploymentsData = deploymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (deploymentsData.length === 0) {
                setCurrentDeployedCompany(null);
                return;
            }

            // Fetch only necessary companies instead of all
            for (const deployment of deploymentsData) {
                const companiesId = (deployment as any).companiesId;
                if (!companiesId) continue;

                // Fetch bank company document
                const bankCompanyDoc = await getDoc(doc(db, "banks", bankId, "companies", companiesId));
                if (bankCompanyDoc.exists()) {
                    const bankCompData = bankCompanyDoc.data();

                    // Check if companyType matches
                    // We need to resolve the companyType (manual vs global)
                    let resolvedType = bankCompData.companyType;
                    let globalData: any = null;

                    if (bankCompData.companyId) {
                        const globalDoc = await getDoc(doc(db, "companies", bankCompData.companyId));
                        if (globalDoc.exists()) {
                            globalData = globalDoc.data();
                            if (!resolvedType) {
                                if (globalData.companyType === 'CCTV' || globalData.companyType === 'BAMS') {
                                    resolvedType = 'Guard Company';
                                } else {
                                    resolvedType = globalData.companyType || 'Guard Company';
                                }
                            }
                        }
                    }

                    if (resolvedType === companyType) {
                        // Create Company object compatible with state
                        const finalCompany: Company = {
                            id: companiesId,
                            bankId: bankId,
                            ...bankCompData,
                            companyName: globalData?.companyName || bankCompData.companyName || 'N/A',
                            companyType: resolvedType,
                            contactEmail: bankCompData.contactEmail || globalData?.contactEmail,
                            email: bankCompData.email || globalData?.email,
                        };
                        setCurrentDeployedCompany(finalCompany);
                        return;
                    }
                }
            }

            setCurrentDeployedCompany(null);
        } catch (error) {
            console.error('Error fetching current deployed company:', error);
            setCurrentDeployedCompany(null);
        } finally {
            setLoadingCurrentCompany(false);
        }
    };

    const openViewModal = async (changeOfVendor: ChangeOfVendor) => {
        setSelectedChangeOfVendor(changeOfVendor);
        setShowViewModal(true);
        await fetchCurrentDeployedCompany(changeOfVendor);
    };

    const handleDeleteRequest = async (request: ChangeOfVendor) => {
        const bankId = changeOfVendors[0]?.bankId || await fetchChangeOfVendorBankId(uid || '', isBankUser);
        if (!bankId) {
            showToast('Bank ID not found', 'error');
            return;
        }

        if (!window.confirm("Are you sure you want to permanently delete this Change of Vendor Request?")) return;

        setLoading(true);
        try {
            await deleteChangeOfVendor(bankId, request.id);
            showToast("Change of Vendor Request deleted successfully", "success");
            
            // Refresh requests list
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid || '', isBankUser, isRegionalHead, psoDesignation, isHead);
            setChangeOfVendors(changeOfVendorsData);
        } catch (error) {
            console.error("Error deleting change of vendor request:", error);
            showToast("Failed to delete Change of Vendor Request", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleChangeOfVendorAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
            setChangeOfVendors(changeOfVendorsData);

            // Extract unique company types
            const typesSet = new Set<string>();
            changeOfVendorsData.forEach(cov => {
                if (cov.company?.companyType) {
                    typesSet.add(cov.company.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            setCurrentPage(1);
            showToast('✅ Change of vendor request submitted successfully!', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing change of vendors:', err);
            showToast('❌ Failed to refresh change of vendors', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.bankId) return;
        setLoading(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }

            const approvedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Head Reviewer';

            await updateChangeOfVendorStatus(bankId, changeOfVendor.id, 'Approved', {
                approvedBy: uid,
                approvedByName
            });

            // Close view modal immediately
            setShowViewModal(false);
            setSelectedChangeOfVendor(null);

            showToast('✅ Change of vendor request approved successfully!', 'success');

            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
            setChangeOfVendors(changeOfVendorsData);
        } catch (err: any) {
            console.error('Error approving change of vendor:', err);
            showToast('❌ Failed to approve request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleForwardToHead = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.bankId) return;
        setLoading(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }

            const forwardedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Regional Reviewer';

            await updateChangeOfVendorStatus(bankId, changeOfVendor.id, 'Forwarded to Head', {
                forwardedBy: uid,
                forwardedByName
            });

            // Close view modal immediately
            setShowViewModal(false);
            setSelectedChangeOfVendor(null);

            showToast('✅ Change of vendor request forwarded to Head successfully!', 'success');

            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
            setChangeOfVendors(changeOfVendorsData);
        } catch (err: any) {
            console.error('Error forwarding change of vendor:', err);
            showToast('❌ Failed to forward request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (changeOfVendor: ChangeOfVendor) => {
        if (!uid || !changeOfVendor.bankId) return;

        if (!window.confirm(`Are you sure you want to cancel request ${changeOfVendor.requestId || changeOfVendor.id}?`)) {
            return;
        }

        setLoading(true);
        try {
            const bankId = changeOfVendor.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }
            // Update status to 'Cancelled' instead of deleting
            await updateChangeOfVendorStatus(bankId, changeOfVendor.id, 'Cancelled' as any);

            // Close view modal immediately
            setShowViewModal(false);
            setSelectedChangeOfVendor(null);

            showToast('✅ Change of vendor request cancelled', 'success');

            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
            setChangeOfVendors(changeOfVendorsData);
        } catch (err: any) {
            console.error('Error cancelling change of vendor:', err);
            showToast('❌ Failed to cancel request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmReject = async () => {
        if (!rejectingRequest || !uid) return;
        if (isRegionalRejection && !rejectReason.trim()) {
            setRejectError('Rejection reason is mandatory for Regional review.');
            return;
        }

        setLoading(true);
        try {
            const bankId = rejectingRequest.bankId || await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }

            const rejectedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Reviewer';

            await updateChangeOfVendorStatus(bankId, rejectingRequest.id, 'Rejected', {
                rejectReason: rejectReason.trim(),
                rejectedBy: uid,
                rejectedByName,
                rejectionStage: isRegionalRejection ? 'Regional' : 'Head'
            });

            // Close modals immediately
            setShowRejectModal(false);
            setRejectingRequest(null);
            setRejectReason('');
            setShowViewModal(false);
            setSelectedChangeOfVendor(null);

            showToast('✅ Change of vendor request rejected', 'success');

            // Refresh data
            const changeOfVendorsData = await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, psoDesignation, isHead);
            setChangeOfVendors(changeOfVendorsData);
        } catch (err: any) {
            console.error('Error rejecting change of vendor:', err);
            showToast('❌ Failed to reject request', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDeploymentDate = async (changeOfVendor: ChangeOfVendor, newDate: string) => {
        if (!newDate || !uid) {
            showToast('Please select a valid date', 'error');
            return;
        }

        setUpdatingDeploymentDate(changeOfVendor.id);
        try {
            const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
            if (!bankId) {
                showToast('❌ Bank ID not found', 'error');
                return;
            }

            const deploymentDateObj = new Date(newDate);
            await updateDeploymentDate(bankId, changeOfVendor.id, deploymentDateObj);
            showToast('✅ Deployment date updated successfully', 'success');

            // Refresh data
            const designation: string | undefined = psoDesignation;
            const changeOfVendorsData = designation
                ? await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, designation, isHead)
                : await fetchChangeOfVendorsData(uid, isBankUser, isRegionalHead, undefined, isHead);
            setChangeOfVendors(changeOfVendorsData);

            // Update selected item if it's the one being updated
            if (selectedChangeOfVendor?.id === changeOfVendor.id) {
                const updated = changeOfVendorsData.find(cov => cov.id === changeOfVendor.id);
                if (updated) {
                    setSelectedChangeOfVendor(updated);
                }
            }

            setEditingDeploymentDate(null);
            setEditingDeploymentDateValue('');
        } catch (err: any) {
            console.error('Error updating deployment date:', err);
            showToast('❌ Failed to update deployment date', 'error');
        } finally {
            setUpdatingDeploymentDate(null);
        }
    };

    const formatDateForInput = (date: any): string => {
        if (!date) return '';
        const dateObj = date?.toDate ? date.toDate() : new Date(date);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to extract text content from HTML
    const stripHtmlTags = (html: string): string => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // Helper function to convert plain text to simple HTML
    const textToHtml = (text: string): string => {
        // Split by double newlines to create paragraphs
        const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
        const htmlParagraphs = paragraphs.map(p => {
            // Replace single newlines with <br>
            const withBreaks = p.trim().split('\n').join('<br>');
            return `<p style="margin-bottom: 15px;">${withBreaks}</p>`;
        });
        return `<div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; line-height: 1.8;">${htmlParagraphs.join('')}</div>`;
    };

    const formatChangeOfVendorEmailBody = async (changeOfVendor: ChangeOfVendor, currentCompany: Company | null) => {
        if (!changeOfVendor.company || !changeOfVendor.branch) {
            return '<p>Incomplete request information</p>';
        }

        const companyType = changeOfVendor.company.companyType || 'System';
        const branchCode = changeOfVendor.branch.branchCode || 'N/A';
        const branchAddress = changeOfVendor.branch.address || 'N/A';
        const companyName = changeOfVendor.company.companyName || 'N/A';
        // Calculate Installation Date: Request Date + 6 days
        const deploymentDate = changeOfVendor.requestDate
            ? (() => {
                const dateObj = changeOfVendor.requestDate?.toDate
                    ? changeOfVendor.requestDate.toDate()
                    : new Date(changeOfVendor.requestDate);

                // Add 6 days
                dateObj.setDate(dateObj.getDate() + 6);

                const year = dateObj.getFullYear();
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const month = monthNames[dateObj.getMonth()];
                const day = String(dateObj.getDate()).padStart(2, '0');
                return `${day}-${month}-${year}`;
            })()
            : 'N/A';

        // Fetch company contact details
        let contactPersonName = 'Sir/Madam';
        let psoName = 'PSO';
        let psoRegion = '';

        try {
            if (changeOfVendor.bankId && changeOfVendor.company.id) {
                const companyDoc = await getDoc(doc(db, "banks", changeOfVendor.bankId, "companies", changeOfVendor.company.id));
                if (companyDoc.exists()) {
                    const companyData = companyDoc.data();
                    contactPersonName = companyData.contactPersonName || contactPersonName;
                }
            }
        } catch (err) {
            console.error('Error fetching company contact details:', err);
        }

        // Get PSO information from the branch's assigned PSO (not the current user)
        try {
            const branchPsoId = changeOfVendor.branch?.psoId;
            if (branchPsoId && changeOfVendor.bankId) {
                // Fetch PSO details from banks/{bankId}/pos/{psoId}
                const psoDoc = await getDoc(doc(db, "banks", changeOfVendor.bankId, "pos", branchPsoId));
                if (psoDoc.exists()) {
                    const psoData = psoDoc.data();
                    psoName = psoData.name || psoName;

                    // Extract region from designation if available
                    const designation = psoData.designation || '';
                    if (designation.includes('North')) {
                        psoRegion = 'North Region';
                    } else if (designation.includes('South')) {
                        psoRegion = 'South Region';
                    } else if (designation.includes('East')) {
                        psoRegion = 'East Region';
                    } else if (designation.includes('West')) {
                        psoRegion = 'West Region';
                    } else if (designation.includes('Central')) {
                        psoRegion = 'Central Region';
                    } else {
                        psoRegion = designation || 'Region';
                    }
                }
            } else if (changeOfVendor.branch?.psoName) {
                // Fallback: use psoName from branch if available (from enriched branch data)
                psoName = changeOfVendor.branch.psoName;
                psoRegion = 'Region'; // Default if we can't determine region
            }
        } catch (err) {
            console.error('Error fetching branch PSO details:', err);
            // Fallback to branch psoName if available
            if (changeOfVendor.branch?.psoName) {
                psoName = changeOfVendor.branch.psoName;
                psoRegion = 'Region';
            }
        }

        // Determine system type text based on company type
        let systemType = '';
        let equipmentText = '';
        if (companyType === 'BAMS') {
            systemType = 'BAMS System';
            equipmentText = 'BAMS equipment';
        } else if (companyType === 'CCTV') {
            systemType = 'CCTV System';
            equipmentText = 'CCTV equipment';
        } else if (companyType === 'Guard Company' || companyType === 'Guarding') {
            systemType = 'Guarding Services';
            equipmentText = 'guarding services';
        } else {
            systemType = `${companyType} System`;
            equipmentText = `${companyType} equipment`;
        }

        // Format contact person name - handle cases where name might have "Mr." prefix
        let formattedContactName = contactPersonName;
        if (formattedContactName && !formattedContactName.toLowerCase().startsWith('mr.')) {
            formattedContactName = `Mr. ${formattedContactName}`;
        }

        // Format PSO name - handle cases where name might have "Mr." prefix
        let formattedPsoName = psoName;
        if (formattedPsoName && !formattedPsoName.toLowerCase().startsWith('mr.')) {
            formattedPsoName = `Mr. ${formattedPsoName}`;
        }

        return `
            <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; line-height: 1.8;">
                <p style="margin-bottom: 20px;">Dear ${formattedContactName}, M/S ${companyName}</p>
                
                <p style="margin-bottom: 15px;">
                    As per Approval from Head Security and in the light of existing SLA, please install New ${systemType} (Free Package) at HBL Branch Code ${branchCode} ${branchAddress} by ${deploymentDate} (6 days from request) with Coordination / Information of PSO
                </p>
                
                <p style="margin-bottom: 20px;">
                    Dear ${formattedPsoName}, ${psoRegion}
                </p>
                
                <p style="margin-bottom: 20px;">
                    Please coordinate with branch and vendor for installation of all ${equipmentText} as per SLA by M/S ${companyName}. In case of requirement of any additional equipment, kindly get approval from Head Security for further work processing.
                </p>
                
                <p style="margin-top: 30px; font-style: italic; color: red;">
                    <strong>Note:</strong> "HBL reserves rights to cancel the subject Work Order if the installation is not completed within the stipulated time. The branch may be allocated to other vendor in case of non-compliance of SLA"
                </p>
            </div>
        `;
    };

    const formatRemovalEmailBody = async (changeOfVendor: ChangeOfVendor, currentCompany: Company) => {
        if (!changeOfVendor.branch || !currentCompany) {
            return '<p>Incomplete information for removal email</p>';
        }

        const companyType = currentCompany.companyType || 'System';
        const branchCode = changeOfVendor.branch.branchCode || 'N/A';
        const branchAddress = changeOfVendor.branch.address || 'N/A';
        const companyName = currentCompany.companyName || 'N/A';

        // Calculate Removal Date: Request Date + 10 days
        let removalDateStr = 'N/A';
        if (changeOfVendor.requestDate) {
            const reqDateObj = changeOfVendor.requestDate?.toDate
                ? changeOfVendor.requestDate.toDate()
                : new Date(changeOfVendor.requestDate);

            // Add 10 days
            reqDateObj.setDate(reqDateObj.getDate() + 10);

            const year = reqDateObj.getFullYear();
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = monthNames[reqDateObj.getMonth()];
            const day = String(reqDateObj.getDate()).padStart(2, '0');
            removalDateStr = `${day}-${month}-${year}`;
        }

        // Fetch company contact details
        let contactPersonName = currentCompany.contactPersonName || 'Sir/Madam';
        let contactEmail = currentCompany.contactEmail || currentCompany.email || 'N/A';
        let contactMobileNumber = currentCompany.contactMobileNumber || 'N/A';

        let psoName = 'PSO';
        let psoRegion = '';

        // Get PSO information
        try {
            const branchPsoId = changeOfVendor.branch?.psoId;
            if (branchPsoId && changeOfVendor.bankId) {
                const psoDoc = await getDoc(doc(db, "banks", changeOfVendor.bankId, "pos", branchPsoId));
                if (psoDoc.exists()) {
                    const psoData = psoDoc.data();
                    psoName = psoData.name || psoName;
                    const designation = psoData.designation || '';
                    if (designation.includes('North')) psoRegion = 'North Region';
                    else if (designation.includes('South')) psoRegion = 'South Region';
                    else if (designation.includes('East')) psoRegion = 'East Region';
                    else if (designation.includes('West')) psoRegion = 'West Region';
                    else if (designation.includes('Central')) psoRegion = 'Central Region';
                    else psoRegion = designation || 'Region';
                }
            } else if (changeOfVendor.branch?.psoName) {
                psoName = changeOfVendor.branch.psoName;
                psoRegion = 'Region';
            }
        } catch (err) {
            console.error('Error fetching branch PSO details:', err);
            if (changeOfVendor.branch?.psoName) {
                psoName = changeOfVendor.branch.psoName;
                psoRegion = 'Region';
            }
        }

        let systemType = '';
        if (companyType === 'BAMS') systemType = 'BAMS systems';
        else if (companyType === 'CCTV') systemType = 'CCTV systems';
        else if (companyType === 'Guard Company' || companyType === 'Guarding') systemType = 'Guarding Services';
        else systemType = `${companyType} System`;

        let formattedContactName = contactPersonName;
        if (formattedContactName && !formattedContactName.toLowerCase().startsWith('mr.') && !formattedContactName.toLowerCase().startsWith('ms.') && !formattedContactName.toLowerCase().startsWith('sir')) {
            formattedContactName = `Mr. ${formattedContactName}`;
        }

        let formattedPsoName = psoName;
        if (formattedPsoName && !formattedPsoName.toLowerCase().startsWith('mr.') && !formattedPsoName.toLowerCase().startsWith('ms.')) {
            formattedPsoName = `Mr. ${formattedPsoName}`;
        }

        // Detailed Vendor Info Block
        const vendorDetailsParams = `
            <strong>Previous Vendor Details:</strong><br>
            Company Name: ${companyName}<br>
            Contact Person: ${contactPersonName}<br>
            Contact Email: ${contactEmail}<br>
            Contact Number: ${contactMobileNumber}
        `;

        return `
            <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; line-height: 1.8;">
                <p style="margin-bottom: 20px;">Dear ${formattedContactName}, M/S ${companyName}</p>
                
                <p style="margin-bottom: 15px;">
                    In the light of our existing SLA, please remove your ${systemType} from <strong>${branchCode} ${branchAddress}</strong> by <strong>${removalDateStr}</strong> (10 days from request) with coordination / information of PSO.
                </p>

                <p style="margin-bottom: 20px;">
                    Dear ${formattedPsoName}, PSO ${psoRegion}
                </p>
                
                <p style="margin-bottom: 20px;">
                    Please Coordinate with M/S ${companyName} for removal of ${systemType}.
                </p>

                <p style="margin-top: 30px; font-style: italic; color: red;">
                    <strong>Note:</strong> "HBL reserves rights to cancel the subject Work Order if the removal is not completed within the stipulated time."
                </p>
            </div>
        `;
    };


    const handlePrepareEmails = async () => {
        if (!selectedChangeOfVendor) return;

        setIsGeneratingPreview(true);
        try {
            // 1. Prepare Installation Email (New Vendor)
            let instEmails: string[] = [];

            // a. Vendor Contact Email
            if (selectedChangeOfVendor.company?.contactEmail) {
                instEmails.push(selectedChangeOfVendor.company.contactEmail);
            } else if (selectedChangeOfVendor.company?.email) {
                instEmails.push(selectedChangeOfVendor.company.email);
            }

            // b. Incharge Email (Current User)
            if (auth.currentUser?.email) {
                instEmails.push(auth.currentUser.email);
            }

            let psoEmail = '';
            const resolvedRegionalEmails: string[] = [];

            // c. Resolve assigned PSO and their managers dynamically (Regional Head and Regional PSO)
            try {
                if (selectedChangeOfVendor.branch?.psoId && selectedChangeOfVendor.bankId) {
                    const bankId = selectedChangeOfVendor.bankId;
                    const assignedPsoId = selectedChangeOfVendor.branch.psoId;

                    // 1. Fetch assigned PSO details
                    const psoDoc = await getDoc(doc(db, "banks", bankId, "pos", assignedPsoId));
                    if (psoDoc.exists()) {
                        const psoData = psoDoc.data();
                        if (psoData.email) psoEmail = psoData.email;

                        // Fallback: Direct regionalHeadId or regionalPsoId fields on the PSO document itself
                        if (psoData.regionalHeadId) {
                            try {
                                const rhDoc = await getDoc(doc(db, "banks", bankId, "pos", psoData.regionalHeadId));
                                if (rhDoc.exists() && rhDoc.data().email) {
                                    resolvedRegionalEmails.push(rhDoc.data().email);
                                }
                            } catch (rhErr) {
                                console.error("Error fetching direct regionalHeadId email:", rhErr);
                            }
                        }
                        if (psoData.regionalPsoId) {
                            try {
                                const rpDoc = await getDoc(doc(db, "banks", bankId, "pos", psoData.regionalPsoId));
                                if (rpDoc.exists() && rpDoc.data().email) {
                                    resolvedRegionalEmails.push(rpDoc.data().email);
                                }
                            } catch (rpErr) {
                                console.error("Error fetching direct regionalPsoId email:", rpErr);
                            }
                        }
                    }

                    // 2. Fetch all staff to check managedPsos subcollections for management hierarchy
                    const staffSnap = await getDocs(collection(db, "banks", bankId, "pos"));
                    const allStaff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));

                    // Filter for regional roles
                    const regionalStaff = allStaff.filter(s =>
                        s.designation &&
                        (s.designation.toLowerCase().includes("regional") ||
                         s.designation.toLowerCase().includes("regional_head") ||
                         s.designation.toLowerCase().includes("regional_pso"))
                    );

                    // Find direct managers (Regional Head or Regional PSO managing this PSO)
                    const directManagers: any[] = [];
                    for (const staff of regionalStaff) {
                        try {
                            const managedSnap = await getDocs(collection(db, "banks", bankId, "pos", staff.id, "managedPsos"));
                            const isManaging = managedSnap.docs.some(d => d.data().psoId === assignedPsoId);
                            if (isManaging) {
                                directManagers.push(staff);
                                if (staff.email) resolvedRegionalEmails.push(staff.email);
                            }
                        } catch (mErr) {
                            console.error(`Error checking managedPsos for staff ${staff.id}:`, mErr);
                        }
                    }

                    // Find transitive managers (e.g. Regional Head managing a Regional PSO)
                    for (const directManager of directManagers) {
                        for (const staff of regionalStaff) {
                            if (staff.id !== directManager.id) {
                                try {
                                    const managedSnap = await getDocs(collection(db, "banks", bankId, "pos", staff.id, "managedPsos"));
                                    const isManaging = managedSnap.docs.some(d => d.data().psoId === directManager.id);
                                    if (isManaging && staff.email) {
                                        resolvedRegionalEmails.push(staff.email);
                                    }
                                } catch (mErr) {
                                    console.error(`Error checking managedPsos transitively for staff ${staff.id}:`, mErr);
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error resolving regional staff hierarchy emails:", err);
            }

            if (psoEmail) instEmails.push(psoEmail);
            resolvedRegionalEmails.forEach(email => instEmails.push(email));


            // Deduplicate
            instEmails = Array.from(new Set(instEmails.filter(e => e && e.trim() !== '')));
            setInstallationRecipients(instEmails);

            const instBody = await formatChangeOfVendorEmailBody(selectedChangeOfVendor, currentDeployedCompany);
            setInstallationBody(instBody);
            setInstallationBodyText(stripHtmlTags(instBody));

            const companyType = selectedChangeOfVendor.company?.companyType || 'System';
            let systemType = '';
            if (companyType === 'BAMS') systemType = 'BAMS System';
            else if (companyType === 'CCTV') systemType = 'CCTV System';
            else if (companyType === 'Guard Company' || companyType === 'Guarding') systemType = 'Guarding Services';
            else systemType = `${companyType} System`;

            const branchCode = selectedChangeOfVendor.branch?.branchCode || 'N/A';
            const branchAddress = selectedChangeOfVendor.branch?.address || 'N/A';
            setInstallationSubject(`Work Order: Installation of New ${systemType} at HBL Branch Code ${branchCode} ${branchAddress}`);


            // 2. Prepare Removal Email (Current Vendor)
            let removeEmails: string[] = [];

            // a. Vendor Contact Email
            if (currentDeployedCompany) {
                if (currentDeployedCompany.contactEmail) removeEmails.push(currentDeployedCompany.contactEmail);
                else if (currentDeployedCompany.email) removeEmails.push(currentDeployedCompany.email);
            }

            // b. Incharge Email (Current User)
            if (auth.currentUser?.email) {
                removeEmails.push(auth.currentUser.email);
            }

            // c. PSO Email (Reuse resolved assigned PSO and manager emails)
            if (psoEmail) removeEmails.push(psoEmail);
            resolvedRegionalEmails.forEach(email => removeEmails.push(email));


            // Deduplicate
            removeEmails = Array.from(new Set(removeEmails.filter(e => e && e.trim() !== '')));
            setRemovalRecipients(removeEmails);

            if (currentDeployedCompany) {
                const removeBody = await formatRemovalEmailBody(selectedChangeOfVendor, currentDeployedCompany);
                setRemovalBody(removeBody);
                setRemovalBodyText(stripHtmlTags(removeBody));

                const removeSystemType = currentDeployedCompany.companyType === 'BAMS' ? 'BAMS System' : currentDeployedCompany.companyType === 'CCTV' ? 'CCTV System' : 'System';
                const removeSubject = `Remove Your ${removeSystemType} from HBL Branch Code ${branchCode} ${branchAddress}`;
                setRemovalSubject(removeSubject);
            } else {
                setRemovalBody('<p>No current vendor found to remove.</p>');
                setRemovalBodyText('No current vendor found to remove.');
                setRemovalSubject('Removal Order: N/A');
            }

            setShowEmailPreview(true);
            setActiveEmailTab('installation'); // Default to installation tab
        } catch (error: any) {
            console.error('Error generating email preview:', error);
            showToast(`Failed to generate email preview: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleSendEmail = async () => {
        if (!selectedChangeOfVendor) return;

        // Check which emails are being sent
        const sendInstallation = installationRecipients.length > 0;
        const sendRemoval = removalRecipients.length > 0;

        if (!sendInstallation && !sendRemoval) {
            showToast('Please add recipients for at least one email.', 'warning');
            return;
        }

        setIsSendingEmail(true);
        let combinedResults = { successful: [] as any[], failed: [] as any[] };

        try {
            // Send Installation Email
            if (sendInstallation) {
                const emailValidation = validateEmails(installationRecipients.join(', '));
                if (emailValidation.isValid) {
                    const htmlBody = textToHtml(installationBodyText);
                    const emailData = {
                        to: emailValidation.valid,
                        subject: installationSubject,
                        body: htmlBody,
                        isHtml: true
                    };
                    const results = await sendEmailsWithErrorHandling(emailData);
                    combinedResults.successful.push(...results.successful);
                    combinedResults.failed.push(...results.failed);
                } else {
                    showToast(`Invalid installation email addresses: ${emailValidation.invalid.join(', ')}`, 'error');
                    setIsSendingEmail(false);
                    return;
                }
            }

            // Send Removal Email
            if (sendRemoval) {
                const emailValidation = validateEmails(removalRecipients.join(', '));
                if (emailValidation.isValid) {
                    const htmlBody = textToHtml(removalBodyText);
                    const emailData = {
                        to: emailValidation.valid,
                        subject: removalSubject,
                        body: htmlBody,
                        isHtml: true
                    };
                    const results = await sendEmailsWithErrorHandling(emailData);
                    combinedResults.successful.push(...results.successful);
                    combinedResults.failed.push(...results.failed);
                } else {
                    showToast(`Invalid removal email addresses: ${emailValidation.invalid.join(', ')}`, 'error');
                    setIsSendingEmail(false);
                    return;
                }
            }

            // Prepare result message
            let resultMessage = "";
            if (combinedResults.successful.length > 0) {
                resultMessage += `✅ Successfully sent ${combinedResults.successful.length} email(s). `;
            }

            if (combinedResults.failed.length > 0) {
                if (resultMessage) resultMessage += "\n";
                resultMessage += `❌ Failed to send to ${combinedResults.failed.length} recipient(s).`;
            }

            showToast(resultMessage, combinedResults.failed.length === 0 ? 'success' : 'warning');

            if (combinedResults.failed.length === 0) {
                setShowEmailPreview(false);
            }
        } catch (error: any) {
            console.error('Error sending email:', error);
            showToast(`Failed to send email: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const covColumns: Column<ChangeOfVendor>[] = [
        {
            header: 'Request ID',
            width: '12%',
            render: (c) => (
                <span className="font-medium text-gray-900 dark:text-gray-100">{c.requestId || 'N/A'}</span>
            ),
        },
        {
            header: 'Branch Code',
            width: '12%',
            render: (c) => (
                <span className="text-gray-900 dark:text-gray-100">{c.branch?.branchCode || 'N/A'}</span>
            ),
        },
        {
            header: 'Branch Address',
            width: '18%',
            className: '!whitespace-normal align-top',
            render: (c) => (
                <span className="break-words text-gray-600 dark:text-gray-400">{c.branch?.address || 'N/A'}</span>
            ),
        },
        {
            header: 'Company Name',
            width: '16%',
            className: '!whitespace-normal align-top',
            render: (c) => (
                <span className="break-words font-medium text-gray-900 dark:text-gray-100">
                    {c.company?.companyName || 'N/A'}
                </span>
            ),
        },
        {
            header: 'Company Type',
            width: '12%',
            render: (c) => (
                <span className="text-gray-900 dark:text-gray-100">{c.company?.companyType || 'N/A'}</span>
            ),
        },
        {
            header: 'Status',
            width: '10%',
            render: (c) => {
                const rawStatus = c.status || 'Pending';
                const st = normalizeChangeOfVendorStatus(rawStatus);
                let cls = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
                if (st === 'approved') {
                    cls = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
                } else if (st === 'rejected') {
                    cls = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
                } else if (st === 'cancelled') {
                    cls = 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
                } else if (rawStatus === 'Forwarded to Head') {
                    cls = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
                }
                return (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
                        {rawStatus}
                    </span>
                );
            },
        },
        {
            header: 'Deployment Date',
            width: '12%',
            render: (c) =>
                editingDeploymentDate === c.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            type="date"
                            value={editingDeploymentDateValue}
                            onChange={(e) => setEditingDeploymentDateValue(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            min={c.requestDate ? formatDateForInput(c.requestDate) : undefined}
                            disabled={updatingDeploymentDate === c.id}
                        />
                        <button
                            type="button"
                            onClick={() => void handleUpdateDeploymentDate(c, editingDeploymentDateValue)}
                            disabled={updatingDeploymentDate === c.id}
                            className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                        >
                            {updatingDeploymentDate === c.id ? '...' : '✓'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setEditingDeploymentDate(null);
                                setEditingDeploymentDateValue('');
                            }}
                            disabled={updatingDeploymentDate === c.id}
                            className="rounded bg-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-400 disabled:opacity-50 dark:bg-gray-600 dark:text-gray-100"
                        >
                            ✕
                        </button>
                    </div>
                ) : (
                    <div
                        className={isPSO ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400' : ''}
                        onClick={() => {
                            if (isPSO) {
                                setEditingDeploymentDate(c.id);
                                setEditingDeploymentDateValue(formatDateForInput(c.deploymentDate));
                            }
                        }}
                        title={isPSO ? 'Click to edit deployment date' : ''}
                    >
                        {c.deploymentDate ? (
                            (c.deploymentDate as any)?.toDate ? (
                                (c.deploymentDate as any).toDate().toLocaleDateString()
                            ) : (
                                new Date(c.deploymentDate as any).toLocaleDateString()
                            )
                        ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                        )}
                    </div>
                ),
        },
        {
            header: 'Approval/Reject Date',
            width: '12%',
            render: (c) =>
                normalizeChangeOfVendorStatus(c.status) === 'approved' && c.approvalDate ? (
                    <span className="text-green-700 dark:text-green-300">
                        {c.approvalDate?.toDate
                            ? c.approvalDate.toDate().toLocaleDateString()
                            : new Date(c.approvalDate as any).toLocaleDateString()}
                    </span>
                ) : normalizeChangeOfVendorStatus(c.status) === 'rejected' && c.rejectDate ? (
                    <span className="text-red-700 dark:text-red-300">
                        {c.rejectDate?.toDate
                            ? c.rejectDate.toDate().toLocaleDateString()
                            : new Date(c.rejectDate as any).toLocaleDateString()}
                    </span>
                ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                ),
        },
        {
            header: 'Action',
            width: '10%',
            render: (c) => (
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => void openViewModal(c)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        aria-label="View change of vendor"
                    >
                        <Icon icon={FiEye} className="mr-1 h-4 w-4" />
                        View
                    </button>
                    {c.status === 'Rejected' && isPSO && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setResubmitRequest(c);
                                setShowAddModal(true);
                            }}
                            className="inline-flex items-center justify-center rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 transition-all hover:border-indigo-500 hover:bg-indigo-50 hover:text-indigo-650 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 dark:border-indigo-600 dark:bg-gray-800 dark:text-indigo-300 dark:hover:border-indigo-400 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400"
                            aria-label="Resubmit change of vendor request"
                        >
                            <Icon icon={FiPlus} className="mr-1 h-4 w-4" />
                            Resubmit
                        </button>
                    )}
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteRequest(c);
                            }}
                            className="inline-flex items-center justify-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-all hover:border-red-500 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 dark:border-red-600 dark:bg-gray-800 dark:text-red-300 dark:hover:border-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-450"
                            aria-label="Delete change of vendor"
                        >
                            <Icon icon={FiTrash2} className="mr-1 h-4 w-4" />
                            Delete
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    const exportToExcel = async () => {
        if (filtered.length === 0) {
            setToast({ show: true, message: 'No change of vendor requests to export', type: 'warning' });
            return;
        }

        try {
            const bankId = changeOfVendors[0]?.bankId || await fetchChangeOfVendorBankId(uid || '', isBankUser);
            if (!bankId) throw new Error('Bank ID not found');

            const [allDeployments, allCompanies] = await Promise.all([
                fetchAllDeployments(bankId),
                fetchCompaniesData(uid || '', isBankUser)
            ]);

            const worksheetData = filtered.map(item => {
                // Resolve Region: Use locationName if available, otherwise fallback to designation logic
                let region = item.branch?.locationName || 'N/A';

                if (region === 'N/A' || !region) {
                    const designation = item.branch?.psoDesignation || '';
                    if (designation.toLowerCase().includes('north')) region = 'North Region';
                    else if (designation.toLowerCase().includes('south')) region = 'South Region';
                    else if (designation.toLowerCase().includes('east')) region = 'East Region';
                    else if (designation.toLowerCase().includes('west')) region = 'West Region';
                    else if (designation.toLowerCase().includes('central')) region = 'Central Region';
                    else if (designation) region = designation;
                }

                if (!region || region === 'N/A') region = 'Region';

                // Resolve Existing Company
                const proposedCompanyType = item.company?.companyType;
                const existingDeployment = allDeployments.find(d =>
                    d.branchId === item.branchId &&
                    allCompanies.find(c => c.id === d.companiesId)?.companyType === proposedCompanyType
                );
                const existingCompany = allCompanies.find(c => c.id === existingDeployment?.companiesId);
                const existingCompanyName = existingCompany?.companyName || 'N/A';

                // Calculate DHA Dates
                const reqDate = item.requestDate?.toDate ? item.requestDate.toDate() : (item.requestDate ? new Date(item.requestDate) : null);

                const addDays = (date: Date, days: number) => {
                    const result = new Date(date);
                    result.setDate(result.getDate() + days);
                    const year = result.getFullYear();
                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const month = monthNames[result.getMonth()];
                    const day = String(result.getDate()).padStart(2, '0');
                    return `${day}-${month}-${year}`;
                };

                const dhaRemoving = reqDate ? addDays(reqDate, 10) : 'N/A';
                const dhaInstalling = reqDate ? addDays(reqDate, 6) : 'N/A';

                return {
                    'Region': region,
                    'Request ID': item.requestId || item.id,
                    'Branch Code': item.branch?.branchCode || 'N/A',
                    'Branch Address': item.branch?.address || 'N/A',
                    'Existing Company': existingCompanyName,
                    'Proposed Company': item.company?.companyName || 'N/A',
                    'Company Type': item.company?.companyType || 'N/A',
                    'Status': item.status || 'Pending',
                    'DHA Installing': dhaInstalling,
                    'DHA Removing': dhaRemoving,
                    'Approval Date': (item as any).approvalDate
                        ? (item as any).approvalDate?.toDate
                            ? (item as any).approvalDate.toDate().toLocaleDateString()
                            : new Date((item as any).approvalDate).toLocaleDateString()
                        : 'N/A',
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'ChangeOfVendor');
            const fileName = `Change_Of_Vendor_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);
            setToast({ show: true, message: 'Excel exported successfully', type: 'success' });
        } catch (error: any) {
            console.error('Error exporting to Excel:', error);
            setToast({ show: true, message: `Export failed: ${error.message}`, type: 'error' });
        }
    };

    const exportToPdf = () => {
        if (filtered.length === 0) {
            setToast({ show: true, message: 'No change of vendor requests to export', type: 'warning' });
            return;
        }
        const docPdf = new jsPDF('l', 'pt', 'a4');
        docPdf.setFontSize(14);
        docPdf.text('Change of Vendor Requests', 40, 40);

        const rows = filtered.map(item => [
            item.requestId || item.id,
            item.branch?.branchCode || 'N/A',
            item.branch?.address || 'N/A',
            item.company?.companyName || 'N/A',
            item.company?.companyType || 'N/A',
            item.status || 'Pending',
            item.deploymentDate
                ? (item.deploymentDate as any)?.toDate
                    ? (item.deploymentDate as any).toDate().toLocaleDateString()
                    : new Date(item.deploymentDate as any).toLocaleDateString()
                : 'N/A',
        ]);

        autoTable(docPdf, {
            startY: 60,
            head: [[
                'Request ID',
                'Branch Code',
                'Branch Address',
                'Company Name',
                'Company Type',
                'Status',
                'Deployment Date',
            ]],
            body: rows,
            styles: { fontSize: 8, cellPadding: 4 },
            headStyles: { fillColor: [41, 98, 255] },
        });

        docPdf.save(`Change_Of_Vendor_${new Date().toISOString().split('T')[0]}.pdf`);
        setToast({ show: true, message: 'PDF exported successfully', type: 'success' });
    };

    return (
        <>
            <div className={summaryMode ? "p-0" : "p-6"}>
                {toast.show && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toast.message}
                                type={toast.type}
                                onClose={() => setToast({ ...toast, show: false })}
                            />
                        </div>
                    </div>
                )}

                {!summaryMode && (
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Change of vendor</h2>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                Manage change of vendor requests
                            </p>
                        </div>
                        {canCreateRequest && (
                            <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Request
                            </Button>
                        )}
                    </div>
                )}

                {/* Search and Filter Controls — same layout as Surveys.tsx */}
                {!summaryMode && (
                    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                        <div className="min-w-0 flex-1">
                            <TextField
                                type="text"
                                className="!h-11"
                                placeholder="Search by Request ID, Branch Code, Company Name, Company Type, or Status..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applySearch();
                                    }
                                }}
                                prefix={<Icon icon={FiSearch} />}
                                suffix={
                                    searchInput ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchInput('');
                                                setAppliedSearchTerm('');
                                                setCurrentPage(1);
                                            }}
                                            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                            aria-label="Clear search"
                                            title="Clear search"
                                        >
                                            <Icon icon={FiX} className="h-4 w-4" />
                                        </button>
                                    ) : null
                                }
                            />
                        </div>
                        <Button onClick={applySearch} className="!h-11 !py-0 flex items-center gap-2">
                            <Icon icon={FiSearch} className="h-4 w-4" />
                            Search
                        </Button>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                onClick={() => setExportMenuOpen((o) => !o)}
                                className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Export
                                <Icon icon={FiChevronDown} className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {exportMenuOpen && (
                                <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            void exportToExcel();
                                        }}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white">XLS</span>
                                        <span>
                                            <span className="block">Excel workbook</span>
                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">Full filtered columns</span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            exportToPdf();
                                        }}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white">PDF</span>
                                        <span>
                                            <span className="block">PDF report</span>
                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">Full list if unfiltered; capped when filtered</span>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                        <Button variant="secondary" onClick={openFilterModal} className="flex items-center gap-2">
                            <Icon icon={FiFileText} className="h-4 w-4" />
                            Filters
                        </Button>
                        {hasActiveCovFilters && (
                            <Button variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        )}
                    </div>
                </div>
                )}

                <Table
                    data={paginatedData}
                    columns={covColumns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={
                        appliedSearchTerm || hasActiveCovFilters
                            ? 'Try adjusting your search or filter'
                            : 'No change of vendor requests available'
                    }
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage,
                    }}
                />
            </div>

            {/* Modals */}
            <AddChangeOfVendorModal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setResubmitRequest(null);
                }}
                onSuccess={() => {
                    handleChangeOfVendorAdded();
                    setResubmitRequest(null);
                }}
                resubmitRequest={resubmitRequest}
            />

            {/* Rejection Reason Modal */}
            <Modal
                isOpen={showRejectModal}
                onClose={() => {
                    setShowRejectModal(false);
                    setRejectingRequest(null);
                    setRejectReason('');
                    setRejectError('');
                }}
                title="Reject Change of Vendor Request"
                size="md"
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                            Rejection Reason {isRegionalRejection && <span className="text-red-500">*</span>}
                        </label>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => {
                                setRejectReason(e.target.value);
                                if (e.target.value.trim()) {
                                    setRejectError('');
                                }
                            }}
                            placeholder={isRegionalRejection ? "Enter reason for rejection (required)..." : "Enter reason for rejection (optional)..."}
                            className="w-full h-32 rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        />
                        {rejectError && (
                            <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                {rejectError}
                            </p>
                        )}
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowRejectModal(false);
                                setRejectingRequest(null);
                                setRejectReason('');
                                setRejectError('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmReject}
                            disabled={loading}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Reject Request
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filter change of vendor"
                size="lg"
            >
                <div className="space-y-4 p-4">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Quick period
                        </label>
                        <select
                            value={draftDateFilter}
                            onChange={(e) => {
                                const v = e.target.value as 'all' | 'daily' | 'weekly' | 'monthly';
                                setDraftDateFilter(v);
                                setDraftRequestDateFrom(null);
                                setDraftRequestDateTo(null);
                            }}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Time</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date range</label>
                        <DatePicker.RangePicker
                            className="w-full [&_.ant-picker]:min-h-[48px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm"
                            value={
                                draftRequestDateFrom && draftRequestDateTo
                                    ? [dayjs(draftRequestDateFrom), dayjs(draftRequestDateTo)]
                                    : null
                            }
                            allowClear
                            format="MMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftRequestDateFrom(null);
                                    setDraftRequestDateTo(null);
                                    return;
                                }
                                setDraftRequestDateFrom(dates[0].format('YYYY-MM-DD'));
                                setDraftRequestDateTo(dates[1].format('YYYY-MM-DD'));
                                setDraftDateFilter('all');
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                        <select
                            value={draftStatusFilter}
                            onChange={(e) => setDraftStatusFilter(e.target.value as CovStatusFilter)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="forwarded_to_head">Forwarded to Head</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Company type</label>
                        <select
                            value={draftCompanyTypeFilter}
                            onChange={(e) => setDraftCompanyTypeFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Types</option>
                            {companyTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={applyFilters}>Apply</Button>
                    </div>
                </div>
            </Modal>

            {/* View Modal */}
            <Modal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedChangeOfVendor(null);
                    setCurrentDeployedCompany(null);
                }}
                title="Change of Vendor Details"
                size="lg"
            >
                {selectedChangeOfVendor && (
                    <div className="p-6">
                        {/* Action Buttons at Top */}
                        <div className="mb-6 flex justify-end gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                            {/* Email Button - Show for Incharge users */}
                            {isIncharge && (
                                <Button
                                    onClick={handlePrepareEmails}
                                    disabled={loading || loadingCurrentCompany || isGeneratingPreview}
                                    className={`flex items-center gap-2 transition-all ${loadingCurrentCompany
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-300 dark:bg-gray-800 dark:text-gray-600 dark:border-gray-700'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    {isGeneratingPreview ? (
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                    ) : (
                                        <Icon icon={FiMail} className="h-4 w-4" />
                                    )}
                                    {isGeneratingPreview ? 'Preparing...' : 'Send Email'}
                                </Button>
                            )}
                            {/* Cancel: only Incharge MIS — Pending or Approved (not Cancelled / Rejected) */}
                            {(() => {
                                const st = normalizeChangeOfVendorStatus(selectedChangeOfVendor.status);
                                const isMisInchargeUser = designationIsMisIncharge(psoDesignation);
                                return (
                                    isMisInchargeUser &&
                                    st !== 'cancelled' &&
                                    st !== 'rejected' &&
                                    (st === 'pending' || st === 'approved')
                                );
                            })() && (
                                <Button
                                    variant="secondary"
                                    onClick={() => handleCancel(selectedChangeOfVendor)}
                                    disabled={loading}
                                    className="flex items-center gap-2 bg-orange-50 hover:bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                                >
                                    <Icon icon={FiX} className="h-4 w-4" />
                                    Cancel
                                </Button>
                            )}
                            
                            {/* Regional Head / Regional PSO Review - when status is Pending */}
                            {selectedChangeOfVendor.status === 'Pending' && isRegionalHead && (
                                <>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setRejectingRequest(selectedChangeOfVendor);
                                            setIsRegionalRejection(true);
                                            setRejectReason('');
                                            setRejectError('');
                                            setShowRejectModal(true);
                                        }}
                                        disabled={loading}
                                        className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                                    >
                                        <Icon icon={FiX} className="h-4 w-4" />
                                        Reject
                                    </Button>
                                    <Button
                                        onClick={() => handleForwardToHead(selectedChangeOfVendor)}
                                        disabled={loading}
                                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        <Icon icon={FiCheck} className="h-4 w-4" />
                                        Forward to Head
                                    </Button>
                                </>
                            )}

                            {/* Head Review (Bank/Head User) - when status is Forwarded to Head */}
                            {selectedChangeOfVendor.status === 'Forwarded to Head' && (isBankUser || isHead) && (
                                <>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setRejectingRequest(selectedChangeOfVendor);
                                            setIsRegionalRejection(false);
                                            setRejectReason('');
                                            setRejectError('');
                                            setShowRejectModal(true);
                                        }}
                                        disabled={loading}
                                        className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                                    >
                                        <Icon icon={FiX} className="h-4 w-4" />
                                        Reject
                                    </Button>
                                    <Button
                                        onClick={() => handleApprove(selectedChangeOfVendor)}
                                        disabled={loading}
                                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                                    >
                                        <Icon icon={FiCheck} className="h-4 w-4" />
                                        Approve
                                    </Button>
                                </>
                            )}

                            {/* Resubmission Mode for PSO - when status is Rejected */}
                            {selectedChangeOfVendor.status === 'Rejected' && isPSO && (
                                <Button
                                    onClick={() => {
                                        setResubmitRequest(selectedChangeOfVendor);
                                        setShowViewModal(false);
                                        setShowAddModal(true);
                                    }}
                                    disabled={loading}
                                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                                >
                                    <Icon icon={FiPlus} className="h-4 w-4" />
                                    Resubmit Request
                                </Button>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.requestId || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    {isBankUser ? (
                                        <div className="mt-1">
                                            <select
                                                value={selectedChangeOfVendor.status || 'Pending'}
                                                onChange={async (e) => {
                                                    const newStatus = e.target.value as any;
                                                    if (!window.confirm(`Are you sure you want to change the status of this request to "${newStatus}"?`)) {
                                                        return;
                                                    }
                                                    setLoading(true);
                                                    try {
                                                        const bankId = selectedChangeOfVendor.bankId || await fetchChangeOfVendorBankId(uid || '', isBankUser);
                                                        if (!bankId) {
                                                            showToast('❌ Bank ID not found', 'error');
                                                            return;
                                                        }
                                                        
                                                        let extraData: any = {};
                                                        if (newStatus === 'Approved') {
                                                            extraData = {
                                                                approvedBy: uid,
                                                                approvedByName: sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Head Reviewer'
                                                            };
                                                        } else if (newStatus === 'Rejected') {
                                                            const reasonPrompt = window.prompt("Enter rejection reason (optional):");
                                                            extraData = {
                                                                rejectReason: reasonPrompt || '',
                                                                rejectedBy: uid,
                                                                rejectedByName: sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Head Reviewer',
                                                                rejectionStage: 'Head'
                                                            };
                                                        } else if (newStatus === 'Forwarded to Head') {
                                                            extraData = {
                                                                forwardedBy: uid,
                                                                forwardedByName: sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Regional Reviewer'
                                                            };
                                                        }
                                                        await updateChangeOfVendorStatus(bankId, selectedChangeOfVendor.id, newStatus, extraData);
                                                        
                                                        // Close view modal immediately
                                                        setShowViewModal(false);
                                                        setSelectedChangeOfVendor(null);

                                                        showToast(`✅ Status updated to ${newStatus} successfully!`, 'success');
                                                        
                                                        // Refresh data
                                                        const changeOfVendorsData = await fetchChangeOfVendorsData(uid || '', isBankUser, isRegionalHead, psoDesignation, isHead);
                                                        setChangeOfVendors(changeOfVendorsData);
                                                    } catch (err: any) {
                                                        console.error('Error updating status:', err);
                                                        showToast('❌ Failed to update status', 'error');
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                                disabled={loading}
                                                className="w-full max-w-[200px] rounded-lg border border-gray-300 bg-white py-1.5 px-3 text-xs font-semibold text-gray-700 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Forwarded to Head">Forwarded to Head</option>
                                                <option value="Approved">Approved</option>
                                                <option value="Rejected">Rejected</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                    ) : (
                                        <p className="mt-1">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                selectedChangeOfVendor.status === 'Approved'
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                    : selectedChangeOfVendor.status === 'Rejected'
                                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                        : selectedChangeOfVendor.status === 'Cancelled'
                                                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                                                            : selectedChangeOfVendor.status === 'Forwarded to Head'
                                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                            }`}>
                                                {selectedChangeOfVendor.status || 'Pending'}
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.branch?.branchCode || 'N/A'}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Address</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.branch?.address || 'N/A'}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Company Type</label>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {selectedChangeOfVendor.company?.companyType || 'N/A'}
                                    </p>
                                </div>

                                {/* Current Deployed Company */}
                                <div className="col-span-2">
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 block">
                                            Current Deployed Company
                                        </label>
                                        {loadingCurrentCompany ? (
                                            <div className="flex items-center gap-2 py-2">
                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent"></div>
                                                <span className="text-sm text-gray-500 dark:text-gray-400">Loading current vendor...</span>
                                            </div>
                                        ) : currentDeployedCompany ? (
                                            <div className="space-y-2">
                                                <div>
                                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Company Name:</span>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {currentDeployedCompany.companyName || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Company Type:</span>
                                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                                        {currentDeployedCompany.companyType || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                                                No company currently deployed for this type
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* New Requested Company */}
                                <div className="col-span-2">
                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                        <label className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 block">
                                            New Requested Company
                                        </label>
                                        {selectedChangeOfVendor.company ? (
                                            <div className="space-y-2">
                                                <div>
                                                    <span className="text-xs font-medium text-blue-800 dark:text-blue-200">Company Name:</span>
                                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                                        {selectedChangeOfVendor.company.companyName || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-xs font-medium text-blue-800 dark:text-blue-200">Company Type:</span>
                                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                                        {selectedChangeOfVendor.company.companyType || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-sm text-blue-700 dark:text-blue-300 italic">
                                                Company information not available
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {selectedChangeOfVendor.requestDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Date</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.requestDate?.toDate ?
                                                selectedChangeOfVendor.requestDate.toDate().toLocaleDateString() :
                                                new Date(selectedChangeOfVendor.requestDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Deployment</label>
                                    {editingDeploymentDate === selectedChangeOfVendor.id ? (
                                        <div className="mt-2 flex items-center gap-2">
                                            <TextField
                                                type="date"
                                                value={editingDeploymentDateValue}
                                                onChange={(e) => setEditingDeploymentDateValue(e.target.value)}
                                                min={selectedChangeOfVendor.requestDate ? formatDateForInput(selectedChangeOfVendor.requestDate) : undefined}
                                                disabled={updatingDeploymentDate === selectedChangeOfVendor.id}
                                                className="w-48"
                                            />
                                            <Button
                                                onClick={() => handleUpdateDeploymentDate(selectedChangeOfVendor, editingDeploymentDateValue)}
                                                disabled={updatingDeploymentDate === selectedChangeOfVendor.id || !editingDeploymentDateValue}
                                                className="px-3 py-1 text-xs"
                                            >
                                                {updatingDeploymentDate === selectedChangeOfVendor.id ? 'Updating...' : 'Save'}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => {
                                                    setEditingDeploymentDate(null);
                                                    setEditingDeploymentDateValue('');
                                                }}
                                                disabled={updatingDeploymentDate === selectedChangeOfVendor.id}
                                                className="px-3 py-1 text-xs"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="mt-1 flex items-center gap-2">
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {selectedChangeOfVendor.deploymentDate ? (
                                                    selectedChangeOfVendor.deploymentDate?.toDate ?
                                                        selectedChangeOfVendor.deploymentDate.toDate().toLocaleDateString() :
                                                        new Date(selectedChangeOfVendor.deploymentDate).toLocaleDateString()
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-500">Not set</span>
                                                )}
                                            </p>
                                            {isPSO && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setEditingDeploymentDate(selectedChangeOfVendor.id);
                                                        setEditingDeploymentDateValue(formatDateForInput(selectedChangeOfVendor.deploymentDate));
                                                    }}
                                                    className="px-2 py-1 text-xs"
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {selectedChangeOfVendor.approvalDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Approval Date</label>
                                        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                                            {selectedChangeOfVendor.approvalDate?.toDate ?
                                                selectedChangeOfVendor.approvalDate.toDate().toLocaleDateString() :
                                                new Date(selectedChangeOfVendor.approvalDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                {selectedChangeOfVendor.rejectDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Reject Date</label>
                                        <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                            {selectedChangeOfVendor.rejectDate?.toDate ?
                                                selectedChangeOfVendor.rejectDate.toDate().toLocaleDateString() :
                                                new Date(selectedChangeOfVendor.rejectDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                {selectedChangeOfVendor.createdAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.createdAt?.toDate ?
                                                selectedChangeOfVendor.createdAt.toDate().toLocaleString() :
                                                new Date(selectedChangeOfVendor.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                                {selectedChangeOfVendor.updatedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Updated At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.updatedAt?.toDate ?
                                                selectedChangeOfVendor.updatedAt.toDate().toLocaleString() :
                                                new Date(selectedChangeOfVendor.updatedAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}

                                {/* Requester Details */}
                                {selectedChangeOfVendor.requestorName && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requestor Name & Designation</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedChangeOfVendor.requestorName} {selectedChangeOfVendor.requestorDesignation ? `(${selectedChangeOfVendor.requestorDesignation})` : ''}
                                        </p>
                                    </div>
                                )}

                                {/* Complaint Start Date */}
                                {selectedChangeOfVendor.complaintStartDate && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Complaint starting Date</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {new Date(selectedChangeOfVendor.complaintStartDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}

                                {/* Branch/PSO Follow-up Dates */}
                                {selectedChangeOfVendor.followUpDates && selectedChangeOfVendor.followUpDates.length > 0 && (
                                    <div className="col-span-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 block mb-1">Branch / PSO Follow-up Dates</label>
                                        <div className="mt-1 flex flex-wrap gap-2">
                                            {selectedChangeOfVendor.followUpDates.map((d: string) => (
                                                <span key={d} className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800">
                                                    {new Date(d).toLocaleDateString()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Reason / Justification */}
                                {selectedChangeOfVendor.reason && (
                                    <div className="col-span-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Reason / Justification</label>
                                        <div className="rounded-lg border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-900 max-h-40 overflow-y-auto">
                                            <Markdown content={selectedChangeOfVendor.reason} />
                                        </div>
                                    </div>
                                )}

                                {/* Resubmission Note */}
                                {selectedChangeOfVendor.resubmitted && (
                                    <div className="col-span-2 rounded-lg border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-800 dark:bg-blue-950/20">
                                        <span className="text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400 font-sans">Resubmission History</span>
                                        <div className="mt-2 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-blue-900 dark:text-blue-200">Resubmitted At</label>
                                                <p className="mt-1 text-sm text-blue-800 dark:text-blue-300 font-medium">
                                                    {selectedChangeOfVendor.resubmittedAt?.toDate ? selectedChangeOfVendor.resubmittedAt.toDate().toLocaleString() : new Date(selectedChangeOfVendor.resubmittedAt).toLocaleString()}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-blue-900 dark:text-blue-200">Resubmission Reason/Note</label>
                                                <p className="mt-1 text-sm text-blue-800 dark:text-blue-300">
                                                    {selectedChangeOfVendor.resubmissionReason || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Forwarded details */}
                                {selectedChangeOfVendor.forwardedBy && (
                                    <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50/30 p-4 dark:border-amber-800/30 dark:bg-amber-950/10">
                                        <span className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 font-sans">Regional Head Forwarding Details</span>
                                        <div className="mt-2 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-amber-900 dark:text-amber-200">Forwarded By</label>
                                                <p className="mt-1 text-sm text-amber-800 dark:text-amber-300 font-medium">
                                                    {selectedChangeOfVendor.forwardedByName || 'Regional Reviewer'}
                                                </p>
                                            </div>
                                            {selectedChangeOfVendor.forwardedAt && (
                                                <div>
                                                    <label className="text-xs font-semibold text-amber-900 dark:text-amber-200">Forwarded At</label>
                                                    <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                                                        {selectedChangeOfVendor.forwardedAt?.toDate ? selectedChangeOfVendor.forwardedAt.toDate().toLocaleString() : new Date(selectedChangeOfVendor.forwardedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Approval details */}
                                {selectedChangeOfVendor.status === 'Approved' && selectedChangeOfVendor.approvedBy && (
                                    <div className="col-span-2 rounded-lg border border-green-200 bg-green-50/50 p-4 dark:border-green-800 dark:bg-green-950/20">
                                        <span className="text-xs font-bold uppercase tracking-wider text-green-700 dark:text-green-400 font-sans">Final Approval Details</span>
                                        <div className="mt-2 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-green-900 dark:text-green-200">Approved By</label>
                                                <p className="mt-1 text-sm text-green-800 dark:text-green-300 font-medium">
                                                    {selectedChangeOfVendor.approvedByName || 'Head Reviewer'} (Bank User)
                                                </p>
                                            </div>
                                            {selectedChangeOfVendor.approvalDate && (
                                                <div>
                                                    <label className="text-xs font-semibold text-green-900 dark:text-green-200">Approved At</label>
                                                    <p className="mt-1 text-sm text-green-800 dark:text-green-300">
                                                        {selectedChangeOfVendor.approvalDate?.toDate ? selectedChangeOfVendor.approvalDate.toDate().toLocaleString() : new Date(selectedChangeOfVendor.approvalDate).toLocaleString()}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Rejection Details */}
                                {selectedChangeOfVendor.status === 'Rejected' && (
                                    <div className="col-span-2 rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-800 dark:bg-red-950/20">
                                        <span className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-400 font-sans">Rejection Details</span>
                                        <div className="mt-2 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-red-900 dark:text-red-200">Rejected By</label>
                                                <p className="mt-1 text-sm text-red-800 dark:text-red-300 font-medium">
                                                    {selectedChangeOfVendor.rejectedByName || 'Reviewer'} ({selectedChangeOfVendor.rejectionStage || 'Regional'} Reviewer)
                                                </p>
                                            </div>
                                            {selectedChangeOfVendor.rejectDate && (
                                                <div>
                                                    <label className="text-xs font-semibold text-red-900 dark:text-red-200">Rejected At</label>
                                                    <p className="mt-1 text-sm text-red-800 dark:text-red-300">
                                                        {selectedChangeOfVendor.rejectDate?.toDate ? selectedChangeOfVendor.rejectDate.toDate().toLocaleString() : new Date(selectedChangeOfVendor.rejectDate).toLocaleString()}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="col-span-2">
                                                <label className="text-xs font-semibold text-red-900 dark:text-red-200">Rejection Reason</label>
                                                <p className="mt-1 text-sm text-red-800 dark:text-red-300 bg-white dark:bg-gray-950/50 p-2.5 rounded border border-red-100 dark:border-red-900/30">
                                                    {selectedChangeOfVendor.rejectReason || 'No reason provided.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>



            {/* Email Preview Modal */}
            <Modal
                isOpen={showEmailPreview}
                onClose={() => {
                    setShowEmailPreview(false);
                }}
                title="Email Preview - Edit Before Sending"
                size="lg"
            >
                <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4 bg-gray-50/50 dark:bg-gray-800/10 rounded-t-lg">
                        <button
                            className={`px-6 py-3 font-semibold text-sm transition-all focus:outline-none ${activeEmailTab === 'installation'
                                ? 'border-b-2 border-blue-600 text-blue-600 bg-white dark:bg-gray-800/20'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                            onClick={() => setActiveEmailTab('installation')}
                        >
                            Installation Email (New Vendor)
                        </button>
                        <button
                            className={`px-6 py-3 font-semibold text-sm transition-all focus:outline-none ${activeEmailTab === 'removal'
                                ? 'border-b-2 border-orange-600 text-orange-600 bg-white dark:bg-gray-800/20'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                            onClick={() => setActiveEmailTab('removal')}
                        >
                            Removal Email (Old Vendor)
                        </button>
                    </div>

                    {/* Installation Tab */}
                    <div className={activeEmailTab === 'installation' ? 'block' : 'hidden'}>
                        <div className="space-y-4">
                            <div className="">
                                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">New Vendor: {selectedChangeOfVendor?.company?.companyName || 'N/A'}</h3>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                        Recipients
                                    </label>
                                    <ChipInput
                                        value={installationRecipients}
                                        onChange={setInstallationRecipients}
                                        placeholder="Add email and press Enter..."
                                        className="w-full"
                                    />
                                </div>
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                        Subject
                                    </label>
                                    <TextField
                                        type="text"
                                        value={installationSubject}
                                        onChange={(e) => setInstallationSubject(e.target.value)}
                                        className="w-full"
                                    />
                                </div>
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                        Preview
                                    </label>
                                    <div
                                        className="bg-white dark:bg-gray-900 rounded border border-gray-300 dark:border-gray-600 p-4 max-h-[300px] overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: installationBody }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Removal Tab */}
                    <div className={activeEmailTab === 'removal' ? 'block' : 'hidden'}>
                        {currentDeployedCompany ? (
                            <div className="space-y-4">
                                <div className="">
                                    <h3 className="font-semibold text-orange-800 dark:text-orange-300 mb-2">Old Vendor: {currentDeployedCompany.companyName}</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                            Recipients
                                        </label>
                                        <ChipInput
                                            value={removalRecipients}
                                            onChange={setRemovalRecipients}
                                            placeholder="Add email and press Enter..."
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                            Subject
                                        </label>
                                        <TextField
                                            type="text"
                                            value={removalSubject}
                                            onChange={(e) => setRemovalSubject(e.target.value)}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                            Preview
                                        </label>
                                        <div
                                            className="bg-white dark:bg-gray-900 rounded border border-gray-300 dark:border-gray-600 p-4 max-h-[300px] overflow-y-auto"
                                            dangerouslySetInnerHTML={{ __html: removalBody }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                <p>No current vendor deployed. Removal email not applicable.</p>
                            </div>
                        )}
                    </div>

                    {isSendingEmail && (
                        <div className="flex items-center justify-center py-4">
                            <div className="text-center">
                                <div className="mb-2 inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-white border-r-transparent"></div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Sending emails...</p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowEmailPreview(false);
                            }}
                            disabled={isSendingEmail}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendEmail}
                            disabled={isSendingEmail}
                            className="flex items-center gap-2"
                        >
                            {isSendingEmail ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                            ) : (
                                <Icon icon={FiMail} className="h-4 w-4" />
                            )}
                            {isSendingEmail ? 'Sending...' : 'Send All Emails'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

